// Hosting subscription status + GST tax invoices for one hosted agent.
// Shared by the agent form's Billing tab (AgentFormModal) and the standalone
// /billing page (reachable from the account dropdown) so a subscribed
// developer never has to remember which agent's modal the invoices live in.
import { useState } from 'react'
import {
  useGetAgentSubscriptionQuery,
  useGetAgentSubscriptionInvoicesQuery,
} from '../../store/api/agentSubscriptionsApi'
import { useGetMessageUnlockInvoicesQuery } from '../../store/api/developerApi'
import { AgentSubscriptionModal } from '../admin/AgentSubscriptionModal'
import { api } from '../../lib/apiClient'
import { ENDPOINTS } from '../../lib/endpoints'
import { useAppSelector } from '../../hooks/useAppDispatch'
import s from '../../styles/pages/DeveloperPage.module.css'

const fmtDate = (iso: string) => new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })
const fmtRupees = (paise: number) => `₹${(paise / 100).toLocaleString('en-IN')}`

const STATUS_LABEL: Record<string, string> = {
  active: '✓ Active', grace: '⚠ Grace period', deactivated: '⛔ Deactivated',
  pending: 'Pending payment', cancelled: 'Cancelled', terminated: 'Terminated',
}
const STATUS_COLOR: Record<string, string> = {
  active: 'var(--success)', grace: 'var(--warning)', deactivated: 'var(--error)',
  terminated: 'var(--error)', pending: 'var(--text-muted)', cancelled: 'var(--text-muted)',
}

export function AgentBillingPanel({ agentId, agentType = 'hosted' }: { agentId: string; agentType?: 'hosted' | 'external' }) {
  const isHosted = agentType === 'hosted'
  const { data: subData, isLoading: subLoading } = useGetAgentSubscriptionQuery(agentId, { skip: !isHosted })
  const { data: invData, isLoading: invLoading } = useGetAgentSubscriptionInvoicesQuery(agentId, { skip: !isHosted })
  const { data: muInvData, isLoading: muInvLoading } = useGetMessageUnlockInvoicesQuery(agentId)
  const { accessToken } = useAppSelector(st => st.auth)
  const [showPayModal, setShowPayModal] = useState(false)
  const [downloadingId, setDownloadingId] = useState<string | null>(null)
  const [openedId, setOpenedId] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [muDownloadingId, setMuDownloadingId] = useState<string | null>(null)
  const [muOpenedId, setMuOpenedId] = useState<string | null>(null)
  const [muError, setMuError] = useState<string | null>(null)

  const sub = subData?.subscription ?? null
  const invoices = invData?.invoices ?? []
  const messageUnlockInvoices = muInvData?.invoices ?? []

  const payCta = !sub || sub.status === 'pending' ? 'Subscribe'
    : sub.status === 'grace' || sub.status === 'deactivated' ? 'Reactivate'
    : 'Renew / change plan'

  const handleDownload = async (invoiceId: string) => {
    // Open the tab synchronously, before the fetch — a browser tab opened
    // after an await no longer counts as "the user just clicked something"
    // and several browsers silently block it. Opening blank first and
    // navigating it once the PDF arrives sidesteps that entirely, and doubles
    // as "view the receipt": it lands in the browser's own PDF viewer, which
    // has its own save/print controls.
    const win = window.open('', '_blank')
    setDownloadingId(invoiceId); setError(null); setOpenedId(null)
    try {
      const { blob, filename } = await api.downloadFile('ai', ENDPOINTS.agentSubscriptions.invoiceDownload(agentId, invoiceId), accessToken)
      const url = URL.createObjectURL(blob)
      if (win) {
        win.location.href = url
      } else {
        // Popup blocked even for the blank tab — fall back to a same-tab download.
        const a = document.createElement('a')
        a.href = url
        a.download = filename ?? 'invoice.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setOpenedId(invoiceId)
      setTimeout(() => setOpenedId(null), 4000)
    } catch {
      win?.close()
      setError('Could not open that invoice. Please try again shortly.')
    } finally {
      setDownloadingId(null)
    }
  }

  const handleMuDownload = async (invoiceId: string) => {
    const win = window.open('', '_blank')
    setMuDownloadingId(invoiceId); setMuError(null); setMuOpenedId(null)
    try {
      const { blob, filename } = await api.downloadFile('ai', ENDPOINTS.developer.messageUnlockInvoiceDownload(agentId, invoiceId), accessToken)
      const url = URL.createObjectURL(blob)
      if (win) {
        win.location.href = url
      } else {
        const a = document.createElement('a')
        a.href = url
        a.download = filename ?? 'invoice.pdf'
        document.body.appendChild(a)
        a.click()
        a.remove()
      }
      setTimeout(() => URL.revokeObjectURL(url), 60_000)
      setMuOpenedId(invoiceId)
      setTimeout(() => setMuOpenedId(null), 4000)
    } catch {
      win?.close()
      setMuError('Could not open that invoice. Please try again shortly.')
    } finally {
      setMuDownloadingId(null)
    }
  }

  return (
    <div>
      {isHosted && (
        <>
          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.6rem' }}>
            Hosting subscription
          </div>

          {subLoading && <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>Loading…</div>}

          {!subLoading && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.75rem 1rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: '1.25rem' }}>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: '0.85rem', fontWeight: 700, color: STATUS_COLOR[sub?.status ?? ''] ?? 'var(--text-primary)' }}>
                  {sub ? (STATUS_LABEL[sub.status] ?? sub.status) : 'No subscription'}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: '0.15rem' }}>
                  {sub?.plan?.name ? `${sub.plan.name} plan` : 'Not subscribed to a hosting plan'}
                  {sub?.status === 'active' && sub.currentPeriodEnd && ` · renews ${fmtDate(sub.currentPeriodEnd)}`}
                  {sub?.status === 'grace' && sub.graceEndDate && ` · grace ends ${fmtDate(sub.graceEndDate)}`}
                  {sub?.status === 'deactivated' && sub.deactivatedAt && ` · deleted after ${fmtDate(new Date(new Date(sub.deactivatedAt).getTime() + 30 * 864e5).toISOString())}`}
                </div>
              </div>
              {sub?.status !== 'terminated' && (
                <button onClick={() => setShowPayModal(true)} className={s.primaryBtn} style={{ fontSize: '0.75rem', padding: '0.35rem 0.8rem', flexShrink: 0 }}>
                  {payCta}
                </button>
              )}
            </div>
          )}

          <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
            Tax invoices
          </div>
          {error && <div style={{ fontSize: '0.75rem', color: 'var(--error)', marginBottom: '0.5rem' }}>{error}</div>}
          {invLoading && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading…</div>}
          {!invLoading && invoices.length === 0 && (
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
              No invoices yet — one is generated after each payment.
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', marginBottom: '1.5rem' }}>
            {invoices.map(inv => (
              <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.78rem', padding: '0.5rem 0.75rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontFamily: 'monospace', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoiceNumber}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                    {fmtDate(inv.issuedAt)} · {fmtRupees(inv.totalPaise)}{inv.isPlaceholder ? ' · SAMPLE' : ''}
                  </div>
                  {openedId === inv.id && (
                    <div style={{ fontSize: '0.68rem', color: 'var(--success)', marginTop: '0.15rem' }}>✓ Opened in a new tab</div>
                  )}
                </div>
                <button onClick={() => void handleDownload(inv.id)} disabled={downloadingId === inv.id} className={s.ghostBtnSm} style={{ fontSize: '0.7rem', flexShrink: 0 }}>
                  {downloadingId === inv.id ? 'Opening…' : '👁 View / Download'}
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <div style={{ fontSize: '0.72rem', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '0.5rem' }}>
        Premium chat invoices
      </div>
      <p style={{ margin: '-0.15rem 0 0.65rem', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
        Generated when a reader pays to unlock one of this agent's chat replies — informational; not yet
        included in your Earnings totals.
      </p>
      {muError && <div style={{ fontSize: '0.75rem', color: 'var(--error)', marginBottom: '0.5rem' }}>{muError}</div>}
      {muInvLoading && <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Loading…</div>}
      {!muInvLoading && messageUnlockInvoices.length === 0 && (
        <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>
          No premium chat unlocks yet.
        </div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
        {messageUnlockInvoices.map(inv => (
          <div key={inv.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', fontSize: '0.78rem', padding: '0.5rem 0.75rem', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8 }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'monospace', color: 'var(--text-primary)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{inv.invoiceNumber}</div>
              <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)', marginTop: '0.1rem' }}>
                {fmtDate(inv.issuedAt)} · {fmtRupees(inv.totalPaise)} paid · your share {fmtRupees(inv.agentPaise)}{inv.isPlaceholder ? ' · SAMPLE' : ''}
              </div>
              {muOpenedId === inv.id && (
                <div style={{ fontSize: '0.68rem', color: 'var(--success)', marginTop: '0.15rem' }}>✓ Opened in a new tab</div>
              )}
            </div>
            <button onClick={() => void handleMuDownload(inv.id)} disabled={muDownloadingId === inv.id} className={s.ghostBtnSm} style={{ fontSize: '0.7rem', flexShrink: 0 }}>
              {muDownloadingId === inv.id ? 'Opening…' : '👁 View / Download'}
            </button>
          </div>
        ))}
      </div>

      {showPayModal && (
        <AgentSubscriptionModal agentId={agentId} onClose={() => setShowPayModal(false)} />
      )}
    </div>
  )
}
